import React, { memo, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import {
	FormControlLabel,
	GenericSwitch,
	HookFormCheckbox,
	HookFormInputGroup,
	HookSelectFormGroup,
	Skeleton,
	TextWithLoader,
} from "components";
import { FormColumn, FormRow } from "components/layout";
import { DebounceTimeout } from "globalConstants";
import { getFormattedDate } from "helpers";
import { useLoader } from "hooks";
import { StatusMethodTypes, getScheduleAFriendlyId } from "mappers";
import { scheduleAStatuses } from "models";
import { useProcessorsQuery } from "queries";
import { scheduleAListService } from "services";
import {
	alphaNumericWithSpacesAndPercentsNormalizer,
	trimNormalizer,
} from "validators";
import { maxLength100, required } from "validators/validation";

import ScheduleAOption from "../scheduleAView/scheduleAOption";

import styles from "./scheduleA.module.scss";

type ScheduleAMainFieldsProps = {
	isEditing?: boolean;
	isLoading?: boolean;
	isParentScheduleA: boolean;
	isAgentLoading: boolean;
	agentName: string;
	agentOfficeId: number;
	scheduleData?: any;
};

export const ScheduleAMainFields = memo(
	({
		isEditing = false,
		isLoading = false,
		isParentScheduleA = false,
		isAgentLoading,
		scheduleData,
		agentName,
		agentOfficeId,
	}: ScheduleAMainFieldsProps) => {
		const { id: agentId } = useParams<{ id: string }>();

		const Loader = useLoader(isLoading);

		const [
			initialAutogenerateScheduleAName,
			setInitialAutogenerateScheduleAName,
		] = useState("");

		const [autogenerateScheduleAName, setAutogenerateScheduleAName] =
			useState("");

		const [isManualEditedScheduleAName, setIsManualEditedScheduleAName] =
			useState(false);

		const [focusScheduleAName, setFocusScheduleAName] = useState(false);

		const { data: processors } = useProcessorsQuery({
			pageName: "AddScheduleA",
			options: {
				select: processors => [
					{ name: "Please Select", id: "" },
					...processors,
				],
			},
		});

		const {
			register,
			setValue,
			watch,

			formState: { errors },
		} = useFormContext();

		const processor = watch("processor");
		const scheduleAName = watch("name");
		const status = watch("status");

		const isAvailable = status?.value === StatusMethodTypes.ACTIVE;

		useEffect(() => {
			const processorName = scheduleData?.processor?.name ?? "";
			const scheduleAName = `${agentName} ${processorName}`;

			setInitialAutogenerateScheduleAName(scheduleAName);
		}, [agentName, scheduleData?.processor?.name]);

		useEffect(() => {
			const processorName = processor?.id ? processor?.name ?? "" : "";
			const scheduleAName = `${agentName} ${processorName}`;

			setAutogenerateScheduleAName(scheduleAName);
		}, [agentName, processor?.id, processor?.name]);

		useEffect(() => {
			if (scheduleData?.name) {
				const isEdited =
					initialAutogenerateScheduleAName !== scheduleData?.name;
				setIsManualEditedScheduleAName(isEdited);
			}
		}, [initialAutogenerateScheduleAName, scheduleData?.name]);

		useEffect(() => {
			if (!isManualEditedScheduleAName) {
				setValue("name", autogenerateScheduleAName);
			}
		}, [autogenerateScheduleAName, isManualEditedScheduleAName, setValue]);

		useEffect(() => {
			if (!focusScheduleAName) return;

			if (scheduleData?.name) {
				const isEdited =
					initialAutogenerateScheduleAName !== scheduleAName;
				setIsManualEditedScheduleAName(isEdited);
			} else {
				const isEdited = autogenerateScheduleAName !== scheduleAName;
				setIsManualEditedScheduleAName(isEdited);
			}
		}, [
			initialAutogenerateScheduleAName,
			autogenerateScheduleAName,
			focusScheduleAName,
			scheduleAName,
			scheduleData?.name,
		]);

		const handleFocusScheduleAName = focus => {
			setFocusScheduleAName(focus);
		};

		const { data: scheduleAItems, isFetching } = useQuery(
			["scheduleAList", agentOfficeId],
			() =>
				scheduleAListService.getCompanyScheduleAList({
					companyId: agentOfficeId,
					agentId,
				}),
			{
				select: data => [
					{ value: "", name: "Not assigned" },
					...data.map(item => ({
						value: item.id,
						name: item.name,
						status: item.status,
					})),
				],
				enabled: !!agentOfficeId,
			},
		);

		// Reset isDefault if Unavailable
		useEffect(() => {
			if (!isAvailable) {
				setValue("is_default", false);
			}
		}, [isAvailable, setValue]);

		return (
			<div className={styles.mainFieldWrapper}>
				{isAvailable && (
					<FormRow className="mb-4">
						<FormColumn data-autotest="scheduleIsDefault">
							<Loader
								skeleton={<Skeleton width="100%" height={34} />}
								component={
									<FormControlLabel
										control={
											<GenericSwitch
												register={register}
												name="is_default"
												id="is_default"
											/>
										}
										label="Default Schedule A"
									/>
								}
							/>
						</FormColumn>
					</FormRow>
				)}
				{isEditing && (
					<FormRow>
						<FormColumn>
							<TextWithLoader
								label="Schedule A ID:"
								value={getScheduleAFriendlyId(scheduleData)}
								loading={isLoading}
								labelCol={6}
								valueCol={6}
							/>
						</FormColumn>
						<FormColumn>
							<TextWithLoader
								label="Created at:"
								value={getFormattedDate({
									date: scheduleData?.created_at,
									format: "DD MMMM YYYY",
									keepLocalTime: true,
								})}
								loading={isLoading}
								labelCol={6}
								valueCol={6}
							/>
						</FormColumn>
					</FormRow>
				)}
				<FormRow>
					<FormColumn>
						<HookFormInputGroup
							name="name"
							register={register}
							label="Schedule A Name"
							validator={{
								required,
								maxLength100,
							}}
							normalizeOnBlur={trimNormalizer}
							normalize={
								alphaNumericWithSpacesAndPercentsNormalizer
							}
							showRequiredMark
							errors={errors}
							blurLoading={isLoading || isAgentLoading}
							onFocus={handleFocusScheduleAName}
						/>
					</FormColumn>
					<FormColumn>
						<HookSelectFormGroup
							options={processors}
							name="processor"
							label="Processor"
							getOptionLabel={o => o.name}
							getOptionValue={o => o.id}
							blurLoading={isLoading}
							isSearchable={false}
							isDisabled={isParentScheduleA}
						/>
					</FormColumn>
				</FormRow>
				<FormRow>
					<FormColumn>
						<HookSelectFormGroup
							name="status"
							options={scheduleAStatuses}
							label="Status"
							showRequiredMark
							blurLoading={isLoading}
							isSearchable={false}
							isDisabled={isParentScheduleA}
						/>
					</FormColumn>
					<FormColumn>
						<HookFormCheckbox
							register={register}
							name="are_fees_not_applicable"
							id="are_fees_not_applicable"
							style={{ paddingTop: "7px" }}
							label="Buy Rates are not applicable"
							blurLoading={isLoading}
							blurWidth="200px"
							disabled={isParentScheduleA}
						/>
					</FormColumn>
				</FormRow>
				<FormRow>
					<FormColumn>
						<HookSelectFormGroup
							label="Copy from Agent Office Schedule A`s?"
							options={scheduleAItems}
							getOptionLabel={ScheduleAOption}
							getOptionValue={option => option.value}
							placeholder="Please Select"
							name="parent"
							items={scheduleAItems}
							isSearchable={false}
							debounceTimeout={DebounceTimeout}
							blurLoading={isLoading || isFetching}
							isDisabled={isFetching}
						/>
					</FormColumn>
				</FormRow>
			</div>
		);
	},
);
